import React, { ReactNode, useContext, useEffect, useRef, useState } from "react";
import { withRouter, RouteComponentProps, CompatMatch } from "tools/routerCompat";
import { MSplitter, Button } from "components/Shared";
import { DataField, Enumeration, EnumerationItem, FormDefinition, FormDefinitionAccountSettings, FormDefinitionValidateResponse, FormDesignerPromoteResponse, Question, RulesetDefinition, Section } from "interfaces/Phoenix.FormsDesigner.Models";
import { getRulesetDefinition, getRulesetSettings, getLinkedFormDefinition, updateRulesetDefinition, validateRulesetDefinition, getPromoteData, promoteRuleset } from "requests/rulesRecommendations";
import { getDesignerFormsList } from "requests/phoenixForms";
import LoadingCover from "components/Shared/LoadingCover";
import RulesEditorPreview from "./RulesEditorPreview";
import RulesEditorProps from "./RulesEditorProps";
import { DialogContext, DialogProviderValue } from "components/Shared/DialogProvider";
import uuid from "uuid/v4";
import NewQuestionDialog, { StagedQuestionsReference } from "../Designer/NewQuestionDialog";
import { createSelectGroupEnumItemId, deepCopy, isQuestionSelectGroup } from "../Designer/helpers";
import { ensureAceLoaded } from "../Designer/CodeEditor/CodeEditor";
import responseHandler from "responseHandler";
import { connect } from "react-redux";
import { setCoreLayoutHeaderVisibility } from "actions/layout";
import ValidationMessages from "../Designer/ValidationMessages";
import WebConfig from "middlewares/WebConfig";
import Select from "react-select";
import { designerRuleDefs } from "../Designer/DesignerQuestionDefinitions";
import useStateSorted from "hooks/useStateSorted";
import { PhoenixFormListItem, PromoteFormData } from "interfaces/Mueller.Business.Models";

type ToastType = "success" | "warning" | "error";

interface Props extends RouteComponentProps {
  match: CompatMatch<{ ruleId: string }>;
  history: any;
  makeToast: (text: string, type: ToastType) => void;
  setHeaderVisibility: (visible: boolean) => void;
}

interface QuestionEditDetails {
  section?: Section;
  repeatOrGroup?: Question;
  modifierQuestionId?: string;
}

interface RulesEditFunctions {
  addNewQuestion: (details: QuestionEditDetails) => void;
  deleteQuestion: (questionId: string) => void;
  reorderSections: (newSectionIndexOrder: number[]) => void;
  reorderQuestions: (details: QuestionEditDetails, newQuestionIdOrder: string[]) => void;
  addNewSection: (belowSectionIndex: number, newSectionTitle?: string) => void;
  renameSection: (sectionIndex: number, sectionTitle: string) => void;
  deleteSection: (sectionIndex: number) => void;
  saveRulesetDef: () => void;
  addListItemToQuestion: (questionId: string, name: string, value: string, xpath?: string, exportId?: string, conditionalDisplay?: string, selectedCondition?: string) => void;
  updateQuestionListItem: (questionId: string, listEditIndex: number, newText: string, newValue: string, xpath?: string, exportId?: string, bindableId?: string, conditionalDisplay?: string, selectedCondition?: string) => void;
  setQuestionListItems: (questionId: string, items: EnumerationItem[]) => void;
  reorderQuestionListItems: (questionId: string, newOrder: string[]) => void;
  deleteListItemFromQuestion: (questionId: string, index: number) => void;
  changeLinkedForm: (sectionIndex: number, newFormId: string) => void;
}

interface RulesClipboardFunctions {
  data: Question[];
  copy: (questionId: string) => void;
  remove: (clipboardIndex: number) => void;
}

export type RulesEditorLastChangeType =
  "initial load" |
  "form saved" |
  "add section" |
  "rename section" |
  "delete section" |
  "add question" |
  "delete question" |
  "move section" |
  "move question" |
  "control field edited" |
  "control list item added" |
  "control list item edited" |
  "control list items changed" |
  "control list reordered" |
  "control list item deleted" |
  "form link updated";

export interface RulesEditorProviderValue {
  ruleId: string;
  rulesetDefinition: RulesetDefinition;
  settings: FormDefinitionAccountSettings;
  getQuestionById: (id: string) => Question;
  setSelectedRule: (question: Question) => void;
  selectedRule: Question;
  refreshRulesetDef: (lastChangeType: RulesEditorLastChangeType) => void;
  rulesetDefinitionUpdatedAt: number;
  editFns: RulesEditFunctions;
  clipboard: RulesClipboardFunctions;
  addQuestionOrder: string[];
  setAddQuestionOrder: (order: string[]) => void;
  lastChangeType: RulesEditorLastChangeType;
  lastChangeData: any;
  isSaving: boolean;
  isLoadingLinkedForm: boolean;
  linkedForm: FormDefinition;
  formsList: PhoenixFormListItem[];
}

export const RulesEditorContext = React.createContext<RulesEditorProviderValue>(null);

const RulesEditor = ({ match: { params: { ruleId } }, history, makeToast, setHeaderVisibility }: Props) => {
  const mounted = useRef(true);
  const contextRef = useRef<RulesEditorProviderValue>(null);

  const dialog = useContext<DialogProviderValue>(DialogContext);

  // Store ruleset def as ref (same pattern as form designer - no point copying mutable data into state)
  const _rulesetDefRef = useRef<RulesetDefinition>(null);
  const rulesetDefinition = _rulesetDefRef.current;
  const [rulesetDefinitionUpdatedAt, _setRulesetDefinitionUpdatedAt] = useState<number>();
  const [rulesetLastLoadedAt, setRulesetLastLoadedAt] = useState<number>();
  const [lastChangeType, setLastChangeType] = useState<RulesEditorLastChangeType>(null);
  const [lastChangeData, setLastChangeData] = useState<any>();
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [genericLoadingMessage, setGenericLoadingMessage] = useState<string>(null);
  const [clipboard, setClipboard] = useState<Question[]>((window as any).rulesEditorClipboard ?? []);
  const historyBlockRef = useRef<Function>(null);
  const [collapsedButtons, setCollapsedButtons] = useState<boolean>();
  const headerRef = useRef<HTMLDivElement>(null);
  const headerResize = useRef<ResizeObserver>(null);
  const [formsList, setFormsList] = useStateSorted<PhoenixFormListItem>(null, "name");
  const [selectedRule, setSelectedRule] = useState<Question>();
  const [addQuestionOrder, _setAddQuestionOrder] = useState<string[]>([]);

  const [settings, setSettings] = useState<FormDefinitionAccountSettings>(null);
  const [linkedForms, setLinkedForms] = useState<Record<string, { definition: FormDefinition, isLoading: boolean }>>({});

  const setLinkedForm = (formDefinitionId: string, formDefinition: FormDefinition) => {
    setLinkedForms(prev => ({ ...prev, [formDefinitionId]: { definition: formDefinition, isLoading: false } }));
  };

  const loadLinkedForm = (formDefinitionId: string) => {
    if (linkedForms[formDefinitionId] || linkedForms[formDefinitionId]?.isLoading) return;
    setLinkedForms(prev => ({ ...prev, [formDefinitionId]: { definition: null, isLoading: true } }));
    getLinkedFormDefinition(formDefinitionId, formDefinition => setLinkedForm(formDefinitionId, formDefinition), null, mounted);
  };

  const refreshRulesetDef = (lastChangeType: RulesEditorLastChangeType, lastChangeData: any = null) => {
    setLastChangeType(lastChangeType);
    setLastChangeData(lastChangeData);
    _setRulesetDefinitionUpdatedAt(Date.now());
  };

  const changeLinkedForm = (sectionIndex: number, newFormId: string) => {
    let section = rulesetDefinition.pages[0].sections[sectionIndex];
    
    if (!newFormId) {
      section.linkedFormDefinitionId = null;
      section.linkedFormDefinitionName = null;
    } else {
      let form = formsList.find(f => f.phoenixFormId === newFormId);
      section.linkedFormDefinitionId = form.phoenixFormId;
      section.linkedFormDefinitionName = form.name;
    }

    refreshRulesetDef("form link updated");
  };

  useEffect(() => {
    setHeaderVisibility(false);

    getRulesetDefinition(ruleId, (rulesetDef) => {
      _rulesetDefRef.current = rulesetDef;
      setRulesetLastLoadedAt(Date.now());
      refreshRulesetDef("initial load");
    }, null, mounted);

    getRulesetSettings(setSettings, null, mounted);
    getDesignerFormsList({ includeInactive: false }, setFormsList, mounted);

    ensureAceLoaded();

    return () => {
      mounted.current = false;
      setHeaderVisibility(true);
      clearLeavePageBlock();
    };
  }, []);

  const activeSection = rulesetDefinition?.pages?.[0].sections.find(s => s.mobileQuestions.includes(selectedRule?.id));
  useEffect(() => {
    if (activeSection) {
      loadLinkedForm(activeSection.linkedFormDefinitionId);
    }
  }, [activeSection]);

  const clearLeavePageBlock = () => {
    // @ts-ignore
    window.onbeforeunload = null;
    historyBlockRef.current?.();
    historyBlockRef.current = null;
  };

  useEffect(() => {
    if (rulesetHasUnsavedChanges()) {
      // @ts-ignore
      window.onbeforeunload = () => true;
      if (!historyBlockRef.current) {
        historyBlockRef.current = history.block(newLocation => {
          if (location.pathname !== newLocation.pathname) {
            return "You have unsaved changes. Are you sure you want to leave this page?";
          }
        });
      }
    } else {
      clearLeavePageBlock();
    }
  }, [lastChangeType]);

  const rulesetHasUnsavedChanges = () => {
    return lastChangeType !== "initial load" && lastChangeType !== "form saved" && lastChangeType;
  };

  const whereIsThisQuestion = (questionId: string): { section?: Section, repeatOrGroup?: Question } => {
    let section = rulesetDefinition.pages[0].sections.find(s => s.mobileQuestions.indexOf(questionId) > -1);
    if (section) return { section };

    return {
      repeatOrGroup: rulesetDefinition.questionLibrary.questions.find(q => q.control.controls?.some(c => c.questionId === questionId))
    };
  };

  const addNewQuestion = (details: QuestionEditDetails, stagedQuestionsReference?: StagedQuestionsReference) => {
    if (stagedQuestionsReference) {
      if (!stagedQuestionsReference.questions?.length && !stagedQuestionsReference.clipboardQuestions?.length) {
        dialog.hide();
        return;
      }

      let idsToAdd = stagedQuestionsReference.questions.map(q => {
        let question = {
          id: uuid(),
          control: deepCopy(q),
          description: "",
          name: "",
        } as Question;

        if (!question.control.dataField) question.control.dataField = {} as DataField;

        let control = question.control;
        if (!control.label) control.label = "";

        control.dataField.enumeration = {
            id: uuid(),
            items: [],
          } as Enumeration;
        
        rulesetDefinition.questionLibrary.questions.push(question);
        return question.id;
      });

      idsToAdd.push(...stagedQuestionsReference.clipboardQuestions.map(q => {
        let question = {
          ...deepCopy(q),
          id: uuid(),
        } as Question;

        rulesetDefinition.questionLibrary.questions.push(question);
        return question.id;
      }));

      let insertIndex: number;

      if (details.section) {
        if (details.modifierQuestionId) insertIndex = details.section.mobileQuestions.findIndex(q => q === details.modifierQuestionId) + 1;
        else insertIndex = details.section.mobileQuestions.length;

        details.section.mobileQuestions.splice(insertIndex, 0, ...idsToAdd);
      }

      refreshRulesetDef("add question");
      dialog.hide();
    } else {
      let stagedQuestionsReference: StagedQuestionsReference = { questions: [], clipboardQuestions: [] };

      dialog.showYesNo(
        null,
        <NewQuestionDialog
          stagedQuestionsReference={stagedQuestionsReference}
          context={contextRef}
          filterOutContainerControls={!!details.repeatOrGroup}
          questionDefinitions={designerRuleDefs}
        />,
        () => addNewQuestion(details, stagedQuestionsReference),
        dialog.hide,
        "Add Control",
        "Cancel",
        { className: "new-question-dialog", useFlexInsteadOfTransformForCentering: true }
      );
    }
  };

  const deleteQuestion = (questionId: string, areYouSure: boolean = false) => {
    let question = getQuestionById(questionId);
    if (!areYouSure) {
      dialog.showYesNo("Delete Question", `Are you sure you want to delete question "${question.control.label}"?`, () => deleteQuestion(questionId, true));
    } else {
      let deleteIndex = rulesetDefinition.questionLibrary.questions.findIndex(q => q.id === questionId);
      let deletedQuestion = rulesetDefinition.questionLibrary.questions.splice(deleteIndex, 1)[0];

      // clean up nested questions inside groups/repeats
      let nestedQuestionIds = deletedQuestion.control.controls?.map(c => c.questionId);
      if (nestedQuestionIds?.length > 0) {
        rulesetDefinition.questionLibrary.questions = rulesetDefinition.questionLibrary.questions.filter(q => !nestedQuestionIds.some(nq => q.id === nq));
      }

      const where = whereIsThisQuestion(questionId);

      if (where.repeatOrGroup) {
        let deleteIndex = where.repeatOrGroup.control.controls.findIndex(c => c.questionId === questionId);
        where.repeatOrGroup.control.controls.splice(deleteIndex, 1);
      }

      if (where.section) {
        let deleteIndex = where.section.mobileQuestions.findIndex(q => q === questionId);
        where.section.mobileQuestions.splice(deleteIndex, 1);
      }

      refreshRulesetDef("delete question");
    }
  };

  const addListItemToQuestion = (questionId: string, name: string, value: string, xpath?: string, exportId?: string, conditionalDisplay?: string, selectedCondition?: string) => {
    let question = getQuestionById(questionId);

    let bindableId: string = undefined;
    if (isQuestionSelectGroup(question)) bindableId = createSelectGroupEnumItemId(question.control.dataField?.bindableId, name);

    question.control.dataField.enumeration.items.push({ name, value: value ?? name, xpath, exportId, bindableId, relevantExpression: conditionalDisplay, selectedExpression: selectedCondition } as EnumerationItem);
    refreshRulesetDef("control list item added");
  };

  const updateQuestionListItem = (questionId: string, listEditIndex: number, newText: string, newValue: string, xpath?: string, exportId?: string, bindableId?: string, conditionalDisplay?: string, selectedCondition?: string) => {
    let question = getQuestionById(questionId).control.dataField.enumeration.items[listEditIndex];

    question.name = newText;
    question.value = newValue;
    question.xpath = xpath;
    question.exportId = exportId;
    question.bindableId = bindableId;
    question.relevantExpression = conditionalDisplay;
    question.selectedExpression = selectedCondition;

    refreshRulesetDef("control list item edited");
  };

  const setQuestionListItems = (questionId: string, items: EnumerationItem[]) => {
    let question = getQuestionById(questionId);
    question.control.dataField.enumeration.items = items;
    refreshRulesetDef("control list items changed");
  };

  const reorderQuestionListItems = (questionId: string, newOrder: string[]) => {
    let question = getQuestionById(questionId);
    question.control.dataField.enumeration.items = newOrder.map(index => question.control.dataField.enumeration.items[parseInt(index)]);
    refreshRulesetDef("control list reordered");
  };

  const deleteListItemFromQuestion = (questionId: string, index: number) => {
    let question = getQuestionById(questionId);
    question.control.dataField.enumeration.items.splice(index, 1);
    refreshRulesetDef("control list item deleted");
  };

  const addNewSection = (belowSectionIndex: number, newSectionTitle?: string) => {
    if (!newSectionTitle) {
      dialog.prompt({
        title: "New Section",
        message: "Enter a name for the new section",
        okLabel: "Add Section",
        onOkClicked: name => addNewSection(belowSectionIndex, name),
      });
    } else {
      let sections = rulesetDefinition.pages[0].sections;
      let newSection = {
        title: newSectionTitle,
        description: "",
        mobileQuestions: [],
        webQuestions: [],
        printQuestions: [],
      } as Section;

      sections.splice(belowSectionIndex + 1, 0, newSection);
      refreshRulesetDef("add section", belowSectionIndex);
    }
  };

  const renameSection = (sectionIndex: number, sectionTitle: string) => {
    rulesetDefinition.pages[0].sections[sectionIndex].title = sectionTitle;
    refreshRulesetDef("rename section");
  };

  const deleteSection = (sectionIndex: number, areYouSure: boolean = false) => {
    if (!areYouSure) {
      dialog.showYesNo("Delete Section", `Are you sure you want to delete ${rulesetDefinition.pages[0].sections[sectionIndex].title} section? This will also delete all questions within the section.`, () => deleteSection(sectionIndex, true));
    } else {
      let sections = rulesetDefinition.pages[0].sections;
      let deleted = sections.splice(sectionIndex, 1)[0];
      rulesetDefinition.questionLibrary.questions = rulesetDefinition.questionLibrary.questions.filter(q => deleted.mobileQuestions.indexOf(q.id) === -1);
      refreshRulesetDef("delete section", sectionIndex);
    }
  };

  const reorderSections = (newSectionIndexOrder: number[]) => {
    rulesetDefinition.pages[0].sections = newSectionIndexOrder.map(i => rulesetDefinition.pages[0].sections[i]);
    refreshRulesetDef("move section");
  };

  const reorderQuestions = (details: QuestionEditDetails, newQuestionIdOrder: string[]) => {
    if (details.section) details.section.mobileQuestions = newQuestionIdOrder;
    if (details.repeatOrGroup) details.repeatOrGroup.control.controls = newQuestionIdOrder.map(id => details.repeatOrGroup.control.controls.find(c => c.questionId === id));

    refreshRulesetDef("move question");
  };

  const setAddQuestionOrder = (questionTitles: string[]) => {
    _setAddQuestionOrder(questionTitles);
  };

  const getQuestionById = (id: string) => rulesetDefinition?.questionLibrary?.questions?.find(q => q.id === id);

  const saveRulesetDef = (successCallback: () => void = null, errorCallback: () => void = null) => {
    setIsSaving(true);
    updateRulesetDefinition(ruleId, rulesetDefinition, () => {
      setIsSaving(false);
      refreshRulesetDef("form saved");
      successCallback?.();
    }, () => {
      setIsSaving(false);
      errorCallback?.();
    }, mounted);
  };

  const saveIfChangesWorkflow = async () => {
    if (rulesetHasUnsavedChanges()) {
      let saveSuccessful = await new Promise(resolve => saveRulesetDef(() => resolve(true), () => resolve(false)));

      if (!saveSuccessful) {
        dialog.showOk("Save failed", "There was an error saving the ruleset definition.");
        return;
      }
    }
  };

  const onBackToListClicked = () => {
    history.push("/phoenixforms-designer/rules-list");
  };

  const copyToClipboard = (questionId: string) => {
    if (clipboard.find(q => q.id === questionId)) {
      makeToast("This item is already on your clipboard", "error");
    } else {
      let copiedQuestion = deepCopy(getQuestionById(questionId));
      let newClipboard = [copiedQuestion, ...clipboard];
      setClipboard(newClipboard);
      (window as any).rulesEditorClipboard = newClipboard;
      makeToast("Item copied to clipboard", "success");
    }
  };

  const removeFromClipboard = (clipboardIndex: number) => {
    let copied = [...clipboard];
    copied.splice(clipboardIndex, 1);
    setClipboard(copied);
    (window as any).rulesEditorClipboard = copied;
  };

  const onPreviewClicked = () => {
    window.open(`${WebConfig.phoenixAnywhereUrl}/services/forms4/preview/designer/ruleset/${ruleId}`, "_blank");
  };

  const validate = async (noDialog = false) => {
    await saveIfChangesWorkflow();

    return await new Promise<FormDefinitionValidateResponse>(resolve => validateRulesetDefinition(ruleId, validationResult => {
      if (!noDialog) dialog.showOk(`Ruleset Validation - ${validationResult.failed ? "Failed" : "Passed"}`, <ValidationMessages validationResult={validationResult} />, null, "Close", { className: "dialog-widthfix" });
      resolve(validationResult);
    }, null, mounted));
  };

  const onValidateClicked = async () => {
    await validate();
  };

  const onPromoteClicked = async () => {
    setGenericLoadingMessage("Validating Ruleset...");
    let results = await validate(true);
    setGenericLoadingMessage(null);

    let stillPromote = true;
    if (results.failed) {
      stillPromote = await new Promise(resolve => dialog.showYesNo(
        "Ruleset Validation - Failed",
        <ValidationMessages validationResult={results} />,
        () => resolve(true),
        () => resolve(false),
        "Override",
        "Close",
        { className: "dialog-widthfix" }
      ));
    }

    if (!stillPromote) return;

    setGenericLoadingMessage("Getting Promote Data...");
    let promoteData = await new Promise<PromoteFormData>(resolve => getPromoteData(ruleId, resolve, () => resolve(null), mounted));
    setGenericLoadingMessage(null);
    if (!promoteData) return;

    stillPromote = await new Promise(resolve => dialog.showYesNo(
      "Destination",
      <div className="promote-details">
        <div className="promote-header">Account</div>
        <div>{promoteData.accountName}</div>
        <div className="promote-header">Ruleset</div>
        <div>{promoteData.formName ?? "New"}</div>
      </div>,
      () => resolve(true),
      () => resolve(false),
      "Promote",
      "Cancel",
      { className: "dialog-widthfix" }
    ));

    if (!stillPromote) return;

    stillPromote = await new Promise(resolve => dialog.showYesNo("Confirm", "Are you sure?", () => resolve(true), () => resolve(false)));
    if (!stillPromote) return;

    setGenericLoadingMessage("Promoting Ruleset...");
    let promoteResponse = await new Promise<FormDesignerPromoteResponse>(resolve => promoteRuleset(ruleId, resolve, () => resolve(null), mounted));
    setGenericLoadingMessage(null);

    if (promoteResponse) {
      dialog.showOk(
        "Success",
        <div className="promote-details">
          <div className="promote-header">Promoted Successfully</div>
          <div className="promote-header">ID:</div>
          <div>{promoteResponse.promotedFormDefinitionId}</div>
        </div>,
        dialog.hide,
        undefined,
        { className: "dialog-widthfix" }
      );
    } else {
      dialog.showOk("Failure", "Promote Failed", dialog.hide);
    }
  };

  const value: RulesEditorProviderValue = {
    ruleId,
    rulesetDefinition,
    settings,
    getQuestionById,
    setSelectedRule,
    selectedRule,
    refreshRulesetDef,
    rulesetDefinitionUpdatedAt,
    addQuestionOrder,
    setAddQuestionOrder,
    lastChangeType,
    lastChangeData,
    isSaving,
    editFns: {
      addNewQuestion,
      deleteQuestion,
      reorderSections,
      reorderQuestions,
      addNewSection,
      renameSection,
      deleteSection,
      saveRulesetDef,
      addListItemToQuestion,
      updateQuestionListItem,
      setQuestionListItems,
      reorderQuestionListItems,
      deleteListItemFromQuestion,
      changeLinkedForm,
    },
    clipboard: {
      data: clipboard,
      copy: copyToClipboard,
      remove: removeFromClipboard,
    },
    isLoadingLinkedForm: linkedForms[activeSection?.linkedFormDefinitionId]?.isLoading ?? false,
    linkedForm: linkedForms[activeSection?.linkedFormDefinitionId]?.definition,
    formsList,
  };

  contextRef.current = value;

  let isLoaded = !!rulesetDefinition && !!settings;

  let loadingMessages: string[] = [];

  if (!isLoaded) loadingMessages.push("Loading Rules Editor...");
  if (isSaving) loadingMessages.push("Saving Ruleset...");
  if (genericLoadingMessage) loadingMessages.push(genericLoadingMessage);

  let isBlocked = !!loadingMessages.length;

  let buttonDefs = [
    { onClick: saveRulesetDef, label: "Save Changes" },
    { onClick: onValidateClicked, label: "Validate" },
    { onClick: onPreviewClicked, label: "Preview" },
    { onClick: onPromoteClicked, label: "Promote" },
    { onClick: onBackToListClicked, label: "Back To Rules List" },
  ];

  let buttons: ReactNode[];

  if (collapsedButtons) {
    buttons = [<Select
                placeholder={"Rule Actions"}
                classNamePrefix="mueller-react-select"
                isSearchable={false}
                value="value prop?"
                options={buttonDefs.map(({ label, onClick }) => ({
                    label,
                    value: onClick,
                  }))}
                onChange={selected => selected.value()}
            />];
  } else {
    buttons = buttonDefs.map(bd => <Button disabled={isBlocked} onClick={bd.onClick}>{bd.label}</Button>);
  }

  const titleResizeObserver = () => {
    if (!headerRef.current) return;
    setCollapsedButtons(headerRef.current.offsetWidth < 900);
  };

  const titleDivResizeListenerRef = (element: HTMLDivElement) => {
    headerRef.current = element;
    if (element) {
      headerResize.current = new ResizeObserver(titleResizeObserver);
      headerResize.current.observe(element);
    } else {
      headerResize.current?.disconnect();
    }
  };

  return (
    <RulesEditorContext.Provider value={value}>
      <div className="phoenixforms-designer">
        <div className="app-header-single-div" ref={titleDivResizeListenerRef}>
          <div className="header-text">{rulesetDefinition ? rulesetDefinition.name : "Loading Rules Editor..."}</div>
          {buttons}
        </div>
        {isLoaded && (
          <MSplitter
            firstChildInitialSize="33%"
            firstChild={<RulesEditorProps key={`k-${rulesetLastLoadedAt}`} />}
            secondChild={<RulesEditorPreview />}
          />
        )}
      </div>
      {!!loadingMessages.length && <LoadingCover loadingMessages={loadingMessages} />}
    </RulesEditorContext.Provider>
  );
};

const mapDispatchToProps = dispatch => ({
  makeToast: (text: string, type: ToastType) => responseHandler.customToast(text, type, dispatch),
  setHeaderVisibility: (visible: boolean) => dispatch(setCoreLayoutHeaderVisibility(visible)),
});

export default withRouter(connect(null, mapDispatchToProps)(RulesEditor));
